import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { DayButton, DayPicker, MonthGrid } from 'react-day-picker';

import 'react-day-picker/style.css';

import type { CaptionLabelProps, ChevronProps, DayButtonProps, DayPickerProps } from 'react-day-picker';

import { cn } from '@/utils/index';
import { Spinner } from '../Spinner/Spinner';

export type CalendarProps = DayPickerProps & {
  contentBelowNavigation?: React.ReactNode;
  bookedDays?: Date[];
  isLoading?: boolean;
};

function CalendarChevron(props: ChevronProps) {
  if (props.orientation === 'left') {
    return <ChevronLeft size={24} {...props} />;
  }
  if (props.orientation === 'right') {
    return <ChevronRight size={24} {...props} />;
  }
  return <ChevronDown size={16} {...props} />;
}

function CustomCaption(props: CaptionLabelProps & { contentBelowNavigation?: React.ReactNode }) {
  return (
    <div className='flex w-full flex-col items-center'>
      <div className='text-body-m-semibold'>{props.children}</div>
      {props.contentBelowNavigation && <div className='mt-5 mb-3 w-full'>{props.contentBelowNavigation}</div>}
    </div>
  );
}

function CustomDay({ isBooked, ...props }: DayButtonProps & { isBooked?: boolean }) {
  const ariaLabel = isBooked ? `${props['aria-label']} (booked)` : props['aria-label'];

  return (
    <DayButton
      {...props}
      aria-label={ariaLabel}
      className={cn(
        {
          "after:bg-text-default-primary disabled:after:bg-text-default-disabled [&.range-middle]:aria-selected:after:bg-border-selected after:absolute after:bottom-0 after:left-1/2 after:size-1 after:-translate-x-1/2 after:-translate-y-1.5 after:rounded-full after:content-['']":
            isBooked,
        },
        props.className,
      )}
    />
  );
}

const CaptionLabelComponent = React.memo<CaptionLabelProps & { contentBelowNavigation?: React.ReactNode }>((props) => <CustomCaption {...props} />);

const DayButtonComponent = React.memo<DayButtonProps & { isBooked?: boolean }>((props) => <CustomDay {...props} />);

function Calendar({ className: classNameProp, classNames, showOutsideDays = true, contentBelowNavigation, bookedDays, isLoading, ...props }: CalendarProps) {
  const getIsDateBooked = React.useCallback((date: Date) => bookedDays?.some((bookedDate) => bookedDate.toDateString() === date.toDateString()), [bookedDays]);

  const captionLabelComponent = React.useMemo(
    () => (captionProps: CaptionLabelProps) => <CaptionLabelComponent {...captionProps} contentBelowNavigation={contentBelowNavigation} />,
    [contentBelowNavigation],
  );

  const dayButtonComponent = React.useMemo(
    () => (dayButtonProps: DayButtonProps) => <DayButtonComponent {...dayButtonProps} isBooked={getIsDateBooked(dayButtonProps.day.date)} />,
    [getIsDateBooked],
  );

  const monthGridComponent = React.useMemo(() => {
    if (isLoading) {
      return () => (
        <div className='flex h-[332px] items-center justify-center'>
          <Spinner className='size-16 stroke-1' />
        </div>
      );
    }
    return MonthGrid;
  }, [isLoading]);

  const components = React.useMemo(
    () => ({
      Chevron: CalendarChevron,
      CaptionLabel: captionLabelComponent,
      DayButton: dayButtonComponent,
      MonthGrid: monthGridComponent,
    }),
    [captionLabelComponent, dayButtonComponent, monthGridComponent],
  );

  const className = {
    // https://daypicker.dev/docs/anatomy
    months: 'relative w-full',
    month: 'flex flex-col gap-3 items-center text-body-s-semibold',
    month_caption: 'flex justify-center relative items-center mb-3 w-full',
    dropdowns: 'relative inline-flex items-center gap-1 text-body-s-semibold',
    caption_label: 'w-full text-body-sm-semibold text-center inline-flex relative items-center whitespace-nowrap',
    nav: 'absolute -top-3 w-full flex justify-between items-center',
    chevron: 'fill-none',
    button_previous: 'hover:opacity-80 p-2.5 aria-disabled:[&>svg]:text-text-default-secondary aria-disabled:pointer-events-none z-10',
    button_next: 'hover:opacity-80 p-2.5 aria-disabled:[&>svg]:text-text-default-secondary aria-disabled:pointer-events-none z-10',
    month_grid: 'w-full border-collapse space-y-1',
    weekdays: 'flex',
    weekday: 'text-text-default-secondary flex-1 text-body-s-semibold',
    weeks: 'flex flex-col',
    week: 'flex w-full mt-2',
    day: 'group h-11 w-[calc(100%/7)] text-body-s-semibold p-0 relative focus-within:relative focus-within:z-20',
    day_button: cn(
      'size-full flex items-center justify-center rounded-button hover:bg-fill-button-secondary-hover active:bg-fill-button-secondary-on-click group-[&.day-range-start.day-range-end]:rounded-button',
    ),
    range_start: 'day-range-start [&>button]:rounded-l-button [&>button]:rounded-r-none',
    range_end: 'day-range-end [&>button]:rounded-r-button [&>button]:rounded-l-none',
    selected: cn(
      'day-selected [&>button]:text-text-default-inverted! [&>button]:bg-fill-button-primary-default [&>button]:hover:bg-fill-button-primary-hover [&>button]:active:bg-fill-button-primary-on-click [&>button]:rounded-button [&.day-selected]:last:[&>button]:rounded-r-button [&.day-selected]:first:[&>button]:rounded-l-button',
      '[&.day-selected>button]:after:bg-text-default-inverted!',
    ),
    today: '',
    outside:
      'day-outside [&.day-outside>button]:text-text-default-secondary [&.day-outside]:pointer-events-none [&.day-outside.range-middle>button]:text-text-default-secondary',
    disabled:
      'text-text-default-disabled bg-fill-background-medium pointer-events-none first:rounded-l-button last:rounded-r-button [&>button]:text-text-default-disabled!',
    range_middle:
      'range-middle [&>button]:rounded-none aria-selected:[&>button]:bg-fill-accent aria-selected:[&>button]:text-text-brand-primary! aria-selected:[&>button]:hover:bg-fill-status-red-accent aria-selected:[&>button]:active:bg-fill-status-red-accent [&.range-middle]:last:[&>button]:rounded-r-button [&.range-middle]:first:[&>button]:rounded-l-button',
    hidden: 'invisible',
    ...classNames,
  };

  return <DayPicker showOutsideDays={showOutsideDays} className={classNameProp} classNames={className} components={components} {...props} />;
}

export { Calendar };
